/* eslint-disable no-console */
import { LogLevel, LogStrategy } from '@ancon/wildcat-utils/logger/types'
import type { Event } from '@bugsnag/js'

import bugsnag from '../../bugsnag'
import isAppProduction from '../features/app/utils/isAppProduction'
import isAppDevelopment from '../features/app/utils/isAppDevelopment'

type LogOptions = {
  /** Whether or not to show warning (YellowBox) */
  warn?: boolean
  /** Whether or not to ignore if error is Axios request */
  ignoreIfRequest?: boolean
}

/** A header's value, whatever the case of its name */
function getHeader(headers: unknown, name: string) {
  if (headers == null || typeof headers !== 'object') return undefined

  const entry = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === name,
  )

  return entry?.[1]
}

/** The URL without its query string and fragment, which can hold customer data */
function stripQuery(url: unknown) {
  return typeof url === 'string' ? url.split(/[?#]/)[0] : undefined
}

/**
 * Only what identifies the request. Headers carry the bearer token and bodies
 * and params can hold customer or payment data, so none of them is sent to
 * Bugsnag
 */
function resolveRequestMetadata(report: Event, properties: any) {
  if (typeof properties !== 'object' || !properties.config) return

  const { url, baseURL, method, endpoint, headers } = properties.config

  report.addMetadata('request', {
    method,
    endpoint,
    url: stripQuery(url),
    baseURL,
    correlationId: getHeader(headers, 'x-correlation-id'),
    clientVersion: getHeader(headers, 'client-version'),
  })
}

/** Status and error code only, no headers or body (see resolveRequestMetadata) */
function resolveResponseMetadata(report: Event, properties: any) {
  if (typeof properties !== 'object' || !properties.response) return

  const { status, data } = properties.response

  report.addMetadata('response', {
    status,
    errorCode: data?.error?.id ?? data?.errorCode,
  })
}

function resolveAdditionalDataMetadata(report: Event, properties: any) {
  if (typeof properties !== 'object' || !properties.additionalData) return

  report.addMetadata('additional data', properties.additionalData)
}

class AppLogStrategy implements LogStrategy {
  public client: typeof bugsnag

  constructor() {
    this.client = bugsnag
  }

  execute(
    logLevel: LogLevel,
    message: string,
    properties?: unknown,
    options?: LogOptions,
  ) {
    switch (logLevel) {
      case LogLevel.Error: {
        this.error(message, properties, options)
        break
      }

      case LogLevel.Information:
        this.info(message, properties)
        break

      case LogLevel.Warning:
        this.warn(message, properties)
        break

      default:
        break
    }
  }

  /**
   * Log messages to console
   * @param title - Title message for the log
   * @param properties - Additional properties to log
   */
  info(title: string, properties: any) {
    try {
      if (!isAppProduction()) {
        console.log(title, properties)
      }

      if (isAppDevelopment()) return

      if (properties) {
        /** Bugsnag only accepts object metadata */
        const isObject =
          typeof properties === 'object' && !Array.isArray(properties)

        if (isObject) {
          this.client.leaveBreadcrumb(title, properties)
        } else {
          console.warn(
            '[Logger] leaveBreadcrumb received metadata array, expected object',
          )
          this.client.leaveBreadcrumb(title, { ...properties })
        }
      } else {
        this.client.leaveBreadcrumb(title, undefined, 'log')
      }
    } catch (err) {
      this.error('[Logger] Error when logging info', err as Error)
    }
  }

  /**
   * Send error to Bugsnag and optionally show YellowBox warning
   * @param title Title message for the error
   * @param err Error object to send to Bugsnag
   * @param options Additional options to support logging
   */
  error = (
    title: string,
    properties: any,
    options: LogOptions = {
      warn: true,
      ignoreIfRequest: true,
    },
  ) => {
    const { warn, ignoreIfRequest } = options

    if (warn) {
      console.warn(title, properties)
    }

    if (isAppDevelopment()) {
      return
    }

    this.client.notify(title, report => {
      try {
        if (typeof properties === 'object') {
          // Don't report to Bugsnag if Axios request error, unless ignoreIfRequest = false
          if (ignoreIfRequest && properties.config) {
            return false
          }

          resolveRequestMetadata(report, properties)
          resolveResponseMetadata(report, properties)
          resolveAdditionalDataMetadata(report, properties)
        }

        return true
      } catch (e) {
        return true
      }
    })
  }

  warn = (title: string, objOrString: any) => {
    console.warn(title, objOrString)
  }
}

export default AppLogStrategy
